/**
 * Cliente de la API.
 *
 * Reglas que se aplican aquí y que corrigen fallos concretos de las
 * aplicaciones anteriores:
 *
 *  · **El token de acceso vive en memoria**, nunca en `localStorage`. Al cerrar
 *    la pestaña desaparece, así que un script inyectado no puede robarlo.
 *  · **Siempre hay tiempo de espera.** En Cuentia ninguna petición lo tenía: si
 *    la red se quedaba colgada, la pantalla se quedaba en «Cargando…» para siempre.
 *  · **Los errores se distinguen por código**, no por el texto del mensaje.
 *    Cuentify y Cuentia detectaban «es un fallo de red» buscando la cadena
 *    `'Failed to fetch'` dentro del mensaje.
 *  · **Un 401 renueva la sesión una vez** y, si no puede, cierra la sesión de
 *    verdad. Nunca se muestran datos de ejemplo haciéndolos pasar por reales,
 *    que es lo que hacía `withFallback` en Cuentia.
 */

import type { ApiErrorCodeValue } from '@eldescuadre/contracts';

/** Cadena vacía = mismo origen (`/api` lo redirige nginx a la API). */
const BASE_URL: string = import.meta.env.VITE_API_URL ?? '';

const DEFAULT_TIMEOUT_MS = 20_000;

/** Token de acceso en memoria. Único sitio donde vive. */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export interface ApiFieldIssue {
  path: string;
  message: string;
}

/** Error de la API con código estable y detalle por campo. */
export class ApiError extends Error {
  readonly code: ApiErrorCodeValue | 'NETWORK' | 'TIMEOUT';
  readonly status: number;
  readonly fields: ApiFieldIssue[];

  constructor(
    code: ApiError['code'],
    message: string,
    status: number,
    fields: ApiFieldIssue[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.fields = fields;
  }

  /** ¿Conviene reintentar más tarde? Solo si el fallo es de red o del servidor. */
  get isRetryable(): boolean {
    return this.code === 'NETWORK' || this.code === 'TIMEOUT' || this.status >= 500;
  }

  get isUnauthorized(): boolean {
    return this.code === 'UNAUTHORIZED' || this.status === 401;
  }

  /** Mensaje por campo, para pintarlo bajo el campo correspondiente. */
  fieldError(path: string): string | undefined {
    return this.fields.find((f) => f.path === path)?.message;
  }

  /**
   * El mensaje con el detalle de qué falló exactamente.
   *
   * ---------------------------------------------------------------------------
   *  Por qué existe esto
   * ---------------------------------------------------------------------------
   *
   * El servidor manda dos cosas: un resumen —«El movimiento está incompleto»— y el
   * detalle de cada campo. Doce pantallas enseñaban **solo el resumen**, así que el
   * usuario leía que algo estaba mal y **no qué**. Eso pasó de verdad: registrar una
   * compra fiada fallaba y no había forma de saber que lo que faltaba era una
   * categoría.
   *
   * Un mensaje de error que no dice qué arreglar no es un mensaje de error: es un
   * callejón sin salida.
   *
   * Los detalles que repitan el resumen se descartan, para no leer lo mismo dos veces.
   */
  get detailMessage(): string {
    const details = this.fields
      .map((field) => field.message)
      .filter((text): text is string => typeof text === 'string' && text.length > 0)
      .filter((text) => text !== this.message);

    return details.length > 0 ? details.join(' ') : this.message;
  }
}

interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null | string[]>;
  /** Validador del contrato. Si falla, es un fallo de programación, no del usuario. */
  schema?: {
    safeParse: (
      input: unknown,
    ) => { success: true; data: T } | { success: false; error: unknown };
  };
  timeoutMs?: number;
  signal?: AbortSignal;
  /** Cabeceras adicionales, como la clave de idempotencia. */
  headers?: Record<string, string>;
  /** Evita reintentar tras renovar (uso interno). */
  skipRefresh?: boolean;
}

function buildUrl(path: string, query?: RequestOptions<unknown>['query']): string {
  const url = `${BASE_URL}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, item);
    } else {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * Renueva la sesión usando la cookie HttpOnly. Devuelve `true` si lo consiguió.
 * Se protege con una única promesa en vuelo para que varias peticiones que
 * fallan a la vez no disparen diez renovaciones.
 */
let refreshInFlight: Promise<boolean> | null = null;

export async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(buildUrl('/api/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
      });

      if (!response.ok) {
        accessToken = null;
        return false;
      }

      const data = (await response.json()) as { accessToken?: string };
      if (typeof data.accessToken !== 'string') {
        accessToken = null;
        return false;
      }
      accessToken = data.accessToken;
      return true;
    } catch {
      accessToken = null;
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function parseError(response: Response): Promise<ApiError> {
  let code: ApiError['code'] = 'INTERNAL_ERROR';
  let message = `Error ${response.status}`;
  let fields: ApiFieldIssue[] = [];

  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string; fields?: ApiFieldIssue[] };
    };
    if (body?.error) {
      code = (body.error.code as ApiError['code']) ?? code;
      message = body.error.message ?? message;
      fields = body.error.fields ?? [];
    }
  } catch {
    // Respuesta sin cuerpo JSON: se conserva el mensaje genérico.
  }

  if (response.status === 401) code = 'UNAUTHORIZED';
  if (response.status === 403) code = 'FORBIDDEN';
  if (response.status === 404) code = 'NOT_FOUND';
  if (response.status === 409) code = 'CONFLICT';
  if (response.status === 429) code = 'RATE_LIMITED';

  return new ApiError(code, message, response.status, fields);
}

/** Realiza una petición a la API. Es el único punto de salida HTTP. */
export async function request<T>(path: string, options: RequestOptions<T> = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    query,
    schema,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    signal,
    headers: extraHeaders,
    skipRefresh = false,
  } = options;

  const headers: Record<string, string> = { Accept: 'application/json', ...extraHeaders };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  // Se combina el tiempo de espera con la señal externa, si la hay.
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  const combinedSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: combinedSignal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'TimeoutError') {
      throw new ApiError('TIMEOUT', 'La operación tardó demasiado. Revisa tu conexión.', 0);
    }
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('NETWORK', 'La operación se canceló.', 0);
    }
    throw new ApiError('NETWORK', 'No hay conexión con el servidor.', 0);
  }

  // Sesión caducada: se intenta renovar una vez y se repite la petición.
  if (response.status === 401 && !skipRefresh) {
    const renewed = await refreshSession();
    if (renewed) {
      return request<T>(path, { ...options, skipRefresh: true });
    }
    throw await parseError(response);
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const data: unknown = text.length > 0 ? JSON.parse(text) : undefined;

  // Validación contra el contrato compartido: si el servidor devuelve algo
  // distinto de lo pactado, se detecta aquí y no en la pantalla del usuario.
  if (schema) {
    const result = schema.safeParse(data);
    if (!result.success) {
      // eslint-disable-next-line no-console
      console.error(`La respuesta de ${method} ${path} no cumple el contrato`, result.error);
      throw new ApiError(
        'INTERNAL_ERROR',
        'El servidor devolvió una respuesta con un formato inesperado.',
        response.status,
      );
    }
    return result.data;
  }

  return data as T;
}

/**
 * Descarga un archivo del servidor y lo guarda en el equipo.
 *
 * ---------------------------------------------------------------------------
 *  Por qué esto no puede ser un enlace normal
 * ---------------------------------------------------------------------------
 *
 * Los botones de descarga eran `<a href="/api/reports/export.csv" download>`. Es lo
 * natural, y **no funciona**, por dos razones que se suman:
 *
 *  1. La sesión viaja en una cabecera `Authorization`, y el token vive **en memoria**.
 *     Un enlace normal no puede poner cabeceras: al pulsarlo, el navegador navega sin
 *     ellas.
 *  2. La cookie de sesión tiene **ruta `/api/auth`**, así que tampoco se envía a
 *     `/api/reports/…`.
 *
 * Resultado: el servidor respondía «Debes iniciar sesión» y el navegador **mostraba ese
 * texto en pantalla** en vez de descargar el archivo. Eso es exactamente lo que pasaba.
 *
 * La solución es pedir el archivo como cualquier otra petición —con la cabecera— y
 * entregar el resultado al navegador como un archivo. Igual que el resto de la
 * aplicación, y con la renovación de sesión incluida.
 */
export async function downloadFile(
  path: string,
  fallbackName = 'eldescuadre.csv',
): Promise<void> {
  const send = async (): Promise<Response> =>
    fetch(buildUrl(path), {
      method: 'GET',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      credentials: 'include',
      signal: AbortSignal.timeout(60_000),
    });

  let response = await send();

  // Sesión caducada: se renueva una vez y se reintenta, igual que en `request`.
  if (response.status === 401) {
    const renewed = await refreshSession();
    if (renewed) response = await send();
  }

  if (!response.ok) throw await parseError(response);

  const blob = await response.blob();

  // El nombre lo decide el servidor, que es quien sabe qué contiene el archivo.
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match?.[1] ?? fallbackName;

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Se libera la memoria del archivo; si no, queda retenido hasta cerrar la pestaña.
  URL.revokeObjectURL(url);
}
